import { RefObject } from "react";
import { SEGMENT_TYPE_ENUM } from "./constants";

export type LassoPointType = {
  x: number;
  y: number;
};

/**
 *
 * @param base64String
 * @returns
 */
export const base64ToImgUrl = (base64String: string) => {
  const mimeType = "image/png";
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  const imgUrl = URL.createObjectURL(blob);
  console.log(" -> imageUtil-base64ToImgUrl, imgUrl:", imgUrl);
  return imgUrl;
};

/**
 *
 * @param file
 * @param fileType
 * @param callback
 */
export const imgFileToBase64 = (
  file: File,
  fileType: string,
  callback: (result: string | ArrayBuffer) => void
) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function (e: ProgressEvent<FileReader>) {
    const readResult = e?.target?.result;
    if (readResult) {
      callback(readResult);
    }
  };
  reader.onerror = function (error) {
    console.error("Error converting image:", fileType, " to Base64:", error);
    callback("");
  };
};

/**
 *
 * @param url
 * @param filename
 * @returns
 */
export const fetchFileByImgUrl = async (url: string, filename: string) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
};

/**
 *
 * @param imageData
 * @param fileName
 * @returns
 */
export function imageDataToFile(
  imageData: ImageData,
  fileName = "new_refer.png"
) {
  // 创建一个 Canvas 元素
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;

  // 将 ImageData 绘制到 Canvas 上
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.putImageData(imageData, 0, 0);

  // 将 Canvas 转换为 Blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], fileName, { type: blob.type });
        resolve(file);
      }
    }, "image/png");
  });
}

/**
 *
 * @param imageData
 * @returns
 */
export function imageDataToBase64(imageData: ImageData) {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.putImageData(imageData, 0, 0);

  // 转换为 Base64 URL
  return canvas.toDataURL("image/png");
}

/**
 *
 * @param base64String
 * @param fileName
 * @returns
 */
export function base64ToFile(base64String: string, fileName: string) {
  // 分离 Base64 数据和 MIME 类型
  const dataPart = base64String.split(",")[1];

  // 将 Base64 数据解码为二进制数据
  const byteString = atob(dataPart);
  const byteNumbers = new Array(byteString.length)
    .fill(0)
    .map((_, i) => byteString.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);

  // 创建 File 对象
  return new File([byteArray], fileName, { type: "image/png" });
}

/**
 *
 * @param segmentMaskUrl
 * @param canvasRef_mask
 * @param canvasRef_maskTemp
 * @param type
 */
export const updateMaskCanvasAfterSegment = (
  segmentMaskUrl: string,
  canvasRef_mask: RefObject<HTMLCanvasElement | null>,
  canvasRef_maskTemp: RefObject<HTMLCanvasElement | null>,
  type: string
) => {
  console.log(
    " -> imageUtil-updateMaskCanvasAfterSegment, segmentMaskUrl:",
    segmentMaskUrl
  );

  if (segmentMaskUrl) {
    const curImg = new Image();
    curImg.crossOrigin = "";
    curImg.src = segmentMaskUrl;
    curImg.onload = async () => {
      const maskCanvas = canvasRef_mask.current;
      if (!maskCanvas) return;

      const maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true });
      if (!maskCtx) return;

      // 获取maskCanvas图片数据
      const maskImgData = maskCtx.getImageData(
        0,
        0,
        maskCanvas.width,
        maskCanvas.height
      );
      if (!maskImgData) return;

      const tempCanvas = canvasRef_maskTemp.current;
      if (!tempCanvas) return;

      const tempCtx = tempCanvas.getContext("2d", { willReadFrequently: true }); // fix warning
      if (!tempCtx) return;

      tempCtx.drawImage(curImg, 0, 0);

      // 获取tempCanvas图片数据
      const tempImgData = tempCtx.getImageData(
        0,
        0,
        tempCanvas.width,
        tempCanvas.height
      );
      if (!tempImgData) return;

      let updatedTempImgDate: ImageData | null = null;

      // rmbg_mask，白色的是人，黑色是背景；白色转透明色，黑色转白色以形成涂抹背景区域
      if (type === SEGMENT_TYPE_ENUM.rmbg) {
        // 转换为透明底0白色255图像
        for (let i = 0; i < tempImgData.data.length; i += 4) {
          if (tempImgData.data[i] > 0) {
            tempImgData.data[i] = 0;
            tempImgData.data[i + 3] = 0;
          } else {
            tempImgData.data[i] = 255;
          }
          if (tempImgData.data[i + 1] > 0) {
            tempImgData.data[i + 1] = 0;
          } else {
            tempImgData.data[i + 1] = 255;
          }
          if (tempImgData.data[i + 2] > 0) {
            tempImgData.data[i + 2] = 0;
          } else {
            tempImgData.data[i + 2] = 255;
          }
        }

        // 更新 Canvas
        tempCtx.putImageData(tempImgData, 0, 0);
      } else {
        /*
         * face_mask/clothe_mask，白色是 face/clothe，黑色是背景，把黑色背景变成透明色
         * 问题二：分割背景后再分割面部，涂抹的背景白色会被面部分割图片的黑色覆盖，如何合并 API 的返回结果
         */
        // 转换为透明底0白色255图像
        for (let i = 0; i < tempImgData.data.length; i += 4) {
          if (tempImgData.data[i] > 0) {
            tempImgData.data[i] = 255;
            tempImgData.data[i + 3] = 255;
          } else {
            tempImgData.data[i] = 0;
            tempImgData.data[i + 3] = 0;
          }
          if (tempImgData.data[i + 1] > 0) {
            tempImgData.data[i + 1] = 255;
            tempImgData.data[i + 3] = 255;
          } else {
            tempImgData.data[i + 1] = 0;
            tempImgData.data[i + 3] = 0;
          }
          if (tempImgData.data[i + 2] > 0) {
            tempImgData.data[i + 2] = 255;
            tempImgData.data[i + 3] = 255;
          } else {
            tempImgData.data[i + 2] = 0;
            tempImgData.data[i + 3] = 0;
          }
        }

        // 更新 Canvas
        tempCtx.putImageData(tempImgData, 0, 0);
      }

      // tempCanvas.style.visibility = "visible";
      updatedTempImgDate = tempImgData;
      // 只把tempCanvas上白色的部分画到maskCanvas
      if (!updatedTempImgDate) return;

      for (let j = 0; j < maskImgData.data.length; j += 4) {
        if (maskImgData.data[j] > 0) {
          maskImgData.data[j] = 255;
          maskImgData.data[j + 3] = 255;
        } else {
          if (updatedTempImgDate.data[j] > 0) {
            maskImgData.data[j] = 255;
            maskImgData.data[j + 3] = 255;
          } else {
            maskImgData.data[j] = 0;
            maskImgData.data[j + 3] = 0;
          }
        }
        if (maskImgData.data[j + 1] > 0) {
          maskImgData.data[j + 1] = 255;
          maskImgData.data[j + 3] = 255;
        } else {
          if (updatedTempImgDate.data[j] > 0) {
            maskImgData.data[j + 1] = 255;
            maskImgData.data[j + 3] = 255;
          } else {
            maskImgData.data[j + 1] = 0;
            maskImgData.data[j + 3] = 0;
          }
        }
        if (maskImgData.data[j + 2] > 0) {
          maskImgData.data[j + 2] = 255;
          maskImgData.data[j + 3] = 255;
        } else {
          if (updatedTempImgDate.data[j] > 0) {
            maskImgData.data[j + 2] = 255;
            maskImgData.data[j + 3] = 255;
          } else {
            maskImgData.data[j + 2] = 0;
            maskImgData.data[j + 3] = 0;
          }
        }
      }

      maskCtx.putImageData(maskImgData, 0, 0);
      // tempCanvas.style.visibility = "hidden";
    }; // curImg.onload
  }
};

/**
 *
 * @param image
 * @param maxWidth
 * @param maxHeight
 * @returns
 */
export const resizeImage = (
  image4Resize: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
  onImgResized: (
    newFile: File | undefined,
    resizedImgWidth: number,
    resizedImgHeight: number
  ) => void
) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) return;

  const { width, height } = image4Resize;
  let resizedImgWidth = width;
  let resizedImgHeight = height;

  if (width > maxWidth || height > maxHeight) {
    const scale = Math.min(maxWidth / width, maxHeight / height);
    resizedImgWidth = Math.floor(width * scale);
    resizedImgHeight = Math.floor(height * scale);
    console.log(
      " -> imageUtil-resizeImage, resizedImgWidth:",
      resizedImgWidth,
      ", resizedImgHeight:",
      resizedImgHeight
    );
  }

  canvas.width = resizedImgWidth;
  canvas.height = resizedImgHeight;
  ctx.drawImage(image4Resize, 0, 0, resizedImgWidth, resizedImgHeight);

  canvas.toBlob(
    (blob) => {
      let newFile: File | undefined = undefined;

      if (blob) {
        newFile = new File([blob], "resized-img.jpg", { type: blob.type });
        console.log(" -> imageUtil-resizeImage, file:", newFile);
      }

      onImgResized(newFile, resizedImgWidth, resizedImgHeight);
    },
    "image/jpeg",
    0.9
  );
};
